import { ScheduleTemplate } from "@/pages/human-resources/time-management/business/schedule-template/ScheduleTemplate.ts";
import { TemplateTimeSlot } from "@/pages/human-resources/time-management/business/time-slot/TemplateTimeSlot.ts";
import { ScheduleTemplateTime } from "@/pages/human-resources/time-management/business/schedule-template/ScheduleTemplateTime.ts";
import { DateTime } from "luxon";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";
import { sortByKey } from "@/shared/utils/Arrays.ts";
import type { HourlyTagCategoryResolver } from "@/pages/human-resources/time-management/business/reference/HourlyTagCategoryResolver.ts";

export interface ScheduleTemplateOut {
  id: number;
  label?: string;
  comment?: string;
  color?: string;
  nbMeals: number;
  horaires?: ScheduleTemplateTimeSlotOut[];
}

export interface ScheduleTemplateTimeSlotOut {
  startTime: string;
  endTime: string;
  timeSlotCategoryId: string | undefined;
  breakTime?: number;
  postId?: number;
}

export function templateMapper(
  resolver: HourlyTagCategoryResolver,
  domainId: string,
  siteId?: string,
): (out: ScheduleTemplateOut) => ScheduleTemplate {
  return (out: ScheduleTemplateOut) =>
    mapToScheduleTemplate(resolver, out, domainId, siteId);
}

function mapToScheduleTemplate(
  categoryResolver: HourlyTagCategoryResolver,
  out: ScheduleTemplateOut,
  domainId: string,
  siteId: string | undefined,
): ScheduleTemplate {
  const timeSlots = (out.horaires ?? []).map((h) =>
    mapToTemplateTimeSlot(categoryResolver, h),
  );
  sortByKey(timeSlots, (ts) => ts.startTime.toMillis());
  return new ScheduleTemplate(
    out.id.toString(),
    domainId,
    timeSlots,
    out.label ?? "",
    out.comment ?? "",
    siteId,
    out.nbMeals,
    out.color,
  );
}

function mapToTemplateTimeSlot(
  resolver: HourlyTagCategoryResolver,
  out: ScheduleTemplateTimeSlotOut,
): TemplateTimeSlot {
  const startTime = mapToScheduleTemplateTime(out.startTime);
  const endTime = mapToScheduleTemplateTime(out.endTime);
  const correctedEndTime = ScheduleTemplateTime.correctedEndTime(
    startTime,
    endTime,
  );
  return new TemplateTimeSlot(
    resolver.idToCategory(out.timeSlotCategoryId),
    out.postId?.toString(),
    out.breakTime ?? 0,
    startTime,
    correctedEndTime,
  );
}

function mapToScheduleTemplateTime(dateStr: string): ScheduleTemplateTime {
  const date = DateTime.fromFormat(dateStr, "dd/MM/yyyy HH:mm:ss", {
    zone: LEGACY_TIME_ZONE,
  }).toJSDate();
  return ScheduleTemplateTime.fromDate(date);
}
